import React from "react";

/**
 * AppErrorBoundary — turns a render crash into a visible message instead of the
 * silent white page React produces when it unmounts the tree. The stack is only
 * shown in development; production gets a reload button.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the full detail in the console for debugging.
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-xl rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-bold text-red-700">Something broke while rendering this page</h1>
          <p className="mt-2 text-sm text-slate-600">
            The page failed to load. Reload to try again — if it keeps happening, the details below
            will help track it down.
          </p>
          {isDev && (
            <pre className="mt-4 max-h-64 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
              {String(error?.stack || error?.message || error)}
            </pre>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-lg bg-[#16213A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#203052]"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
